import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import en from '../locales/en/translation.json';
import hr from '../locales/hr/translation.json';
import ro from '../locales/ro/translation.json';

// The languages ProRef ships (M15 slice 1). Adding a fourth is a new locale file + one entry
// here — every screen that reads text via t() picks it up automatically, no other code change.
export const SUPPORTED_LANGUAGES = ['en', 'hr', 'ro'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

// A language's own name, in that language — NOT translated per-locale (a picker that shows
// "Croatian"/"Hrvatski"/"Croată" depending on which language is currently active is unreadable to
// someone hunting for their own language in a list they can't read yet).
export const LANGUAGE_NAMES: Record<SupportedLanguage, string> = {
  en: 'English',
  hr: 'Hrvatski',
  ro: 'Română',
};

// Resources are bundled at build time (no i18next-http-backend): the whole app's text is small
// enough that per-namespace lazy-loading would add complexity for no real payload win, and it
// keeps translation behaving identically in tests and in the browser — no network/mock needed.
i18n
  // Reads a stored choice first, then the browser's own language, and persists a later change —
  // see `detection` below for exactly where.
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      hr: { translation: hr },
      ro: { translation: ro },
    },
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES,
    // Strip region subtags ("en-US" → "en") before matching against supportedLngs, so a browser
    // set to en-GB or hr-HR still resolves instead of silently falling back to English.
    load: 'languageOnly',
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'proref_language',
    },
    interpolation: { escapeValue: false }, // React already escapes interpolated values.
  });

export default i18n;
